import { observable } from 'mobx';

const NALBROptions = ['N/A', 'L', 'R', 'B/L'];

export interface Pair {
	l: number & number[];
	r: number & number[];
}

export interface Property {
	display: string;
	options: string[];
	description: string;
	value: Pair & number[] & number;
	subProperty?: Property;
}

function generateOptions(from: number, to: number, step: number, suffix: string = 'mm') {
	let arr: string[] = [];

	for (let i = from; i <= to; i += step) {
		if (suffix) {
			arr.push(`${i} ${suffix}`);
		} else {
			arr.push(`${i}`);
		}
	}

	return arr;
}

const shellModifications = {
	shellThickness: {
		display: 'Shell Rigidity',
		// options: generateOptions(1, 5, 1, 'mm'),
		options: ['Rigid', 'Semi-Rigid', 'Flexible'],
		description:
			'Rigidity is determined by patient weight, shell thickness, arch height and foot mobility. We recommend Semi-Rigid for most patients. For heavier patients, patients with low arches, or for very flexible foot types we recommend Rigid. Flexible should be used for patients that will not tolerate a Rigid orthotic or do not require a high degree of correction. EVA should be used for an accommodative device only.',
		value: 1
	} as Property,
	shellShape: {
		display: 'Shell Shape',
		options: ['Standard', 'Hourglass', 'Medial Deformity', 'Lateral Deformity'],
		description:
			'Our default shell shape is a standard shape that is used for most patients. A narrow shell is used for patients with narrow feet or shoes. A wide shell is used for patients with wide feet or shoes. A wide shell will increase the overall width of the orthosis by 1/8” on each side.',
		value: 0
	} as Property,
	firstRayCutout: {
		display: '1st Ray Cut out',
		options: NALBROptions,
		description:
			'The first ray cut out removes the plastic shell under the medial distal edge of the orthosis as shown in the area in gray. This is will allow the first ray to extend its range of plantarflexion.',
		value: 0
	} as Property,
	heelCutout: {
		display: 'Heel Cut Out',
		options: NALBROptions,
		description:
			'Can be used to either reduce bulk in a shallow shoe, like a dress shoe, or to accommodate heel pain from a spur or plantar fasciitis. The standard heel cutout is 1”. The cutout does not include a fill.',
		value: 0
	} as Property,
	flexibleMortonExt: {
		display: "Flexible Morton's Ext",
		options: NALBROptions,
		description:
			'A Flexible Morton’s Extension is printed into the shell and allows for full range of motion at the first MPJ. Note: For an EVA extension, please select Morton’s Ext (EVA) in the Accommodations sections',
		value: 0
	} as Property,
	semiRigidMortonExt: {
		display: "Semi-Rigid Morton's Ext",
		options: NALBROptions,
		description:
			"A Semi-Rigid Morton's extension is printed into the shell and limits dorsiflexion to 45 degrees maximum at the first MPJ. Note: For an EVA extension, please select Morton’s Ext (EVA) in the Accommodations sections.",
		value: 0
	} as Property,
	reverseMortonsExt: {
		display: "Reverse Morton's Ext",
		options: NALBROptions,
		description:
			"The reverse Morton's extension is an semi-rigid extension distally of the shell under the 2-5 MPJ extending to the sulcus. This extension is flexible so it will not impede the extension of the MPJ’s or propulsion of the foot. It is used to offload and increase range of motion of the 1st MTP.",
		value: 0
	} as Property,
	heelSeat: {
		display: 'Heel Seat',
		options: ['Dress 1/4"', 'Std 3/8"', 'Sport 1/2"', 'Deep 5/8"'],
		description:
			'Our default is 3/8” which should accommodate most shoes, and is recommended for dress or low-profile shoes. We recommend Sport depth (1/2”) for increased heel stability and athletic footwear. A Deep heel seat (5/8”) should be used when increased correction is required, or when a heel skive or heel padding is prescribed. Deep heel seats can generally be accommodated by athletic and other high volume shoes.',
		value: 1
	} as Property,
	archModification: {
		display: 'Arch Modification',
		options: ['N/A', 'Increase 1/8"', 'Decrease 1/8"'],
		description:
			'Increasing or decreasing the arch height will have a correlative impact on the degree of correction and shell confirmation to the anatomy. Treatment objectives and patient tolerance should be considered when selecting these options. For patients with hypermobility and low medial arches, increasing the height of the medial arch will decrease the flexibility of the orthotic in this area and may offer more control.',
		value: 0
	} as Property,
	archModificationAmount: {
		display: 'Arch Modification',
		options: ['none', 'Increase 1/8"', 'Decrease 1/8"'],
		description:
			'Increasing or decreasing the arch height will have a correlative impact on the degree of correction and shell confirmation to the anatomy. Treatment objectives and patient tolerance should be considered when selecting these options. For patients with hypermobility and low medial arches, increasing the height of the medial arch will decrease the flexibility of the orthotic in this area and may offer more control.',
		value: { l: 0, r: 0 }
	} as Property,
	medialFlange: {
		display: 'Medial Flange',
		options: NALBROptions,
		description:
			'Medial flanges are used to increase correction by extending the height of the medial border of the orthosis. This modification will widen the overall profile of the orthosis and appropriate footwear need to be recommended. This modification is used to treat severe compensated foot types including Adult Onset Flatfoot, Posterior Tibial Tendonitis and pediatrics. The default height of our medial flange is approximately 1/2”.',
		value: 0
	} as Property,
	lateralFlange: {
		display: 'Lateral Flange',
		options: NALBROptions,
		description:
			' A lateral flange increases the height of the lateral border of the orthosis. This modification will widen the overall profile of the orthosis and appropriate footwear need to be recommended. This modification is used to control abduction of the forefoot. The default height of our lateral flange is approximately 1/4”.',
		value: 0
	} as Property
};

const postingOptions = {
	heelLift: {
		display: 'Heel Lift',
		options: generateOptions(0, 8, 1, 'mm'),
		description:
			'Heel Lift will result in added thickness underneath the heel cup area of the orthoses. Heel Lifts are used to address a variety of conditions including, limb length difference, equinus conditions, Achilles tendonitis and plantar fasciitis. They can be used unilaterally or bilaterally and will take up more room in shoes.',
		value: { l: 0, r: 0 }
	} as Property,
	medialHeelSkive: {
		display: 'Medial Heel Skive',
		options: generateOptions(0, 8, 2, 'mm'),
		description:
			'The Medial Heel Skive creates a varus heel post inside the heel cup instead of an extrinsic heel post.  We recommend a Deep Heel Seat (5/8”) to accommodate a skive.',
		value: { l: 0, r: 0 }
	} as Property,
	lateralHeelSkive: {
		display: 'Lateral Heel Skive',
		options: generateOptions(0, 8, 2, 'mm'),
		description:
			'The Lateral Heel Skive creates a valgus heel post inside the heel cup instead of an extrinsic heel post.  We recommend a Deep Heel Seat (5/8”) to accommodate a skive.',
		value: { l: 0, r: 0 }
	} as Property,
	rearfootPost: {
		display: 'Varus Rearfoot Post',
		options: ['N/A', 'Lab Selects', 'Specify Values'],
		description:
			'The Rearfoot Post is an extrinsic varus post/wedge where material is added to the inferior surface of the heel at an inverted angle.  It is used to stabilize and decelerate the foot and reduce pronation during gait. A rearfoot post will add a small amount of bulk to the device.',
		subProperty: {
			display: 'Specify Values',
			options: generateOptions(0, 6, 1, '°'),
			description: '',
			value: { l: 0, r: 0 }
		} as Property,
		value: 0
	} as Property,
	forefootPost: {
		display: 'Forefoot Post',
		options: ['N/A', 'Balance to rearfoot', 'Extrinsic Specify Values', 'Intrinsic Specify Values'],
		description:
			'The Forefoot Post tilts either the medial or lateral front edge of the orthoses to the ground. It is used when the forefoot to rearfoot relationship is not perpendicular. By using the Forefoot Post to restore that proper relationship compensation can be minimized or eliminated. Forefoot Posts can be either Varus or Valgus. \n \nAn Intrinsic forefoot post manipulates the angle of the distal end of the shell in the design engine. An Extrinsic forefoot post is created by adding a wedge under the distal end of the orthotic shell during post-production.',
		subProperty: {
			display: 'Forefoot type',
			options: ['Varus', 'Valgus'],
			description: '',
			value: { l: 0, r: 0 },
			subProperty: {
				display: 'Forefoot value',
				options: generateOptions(0, 6, 1, '°'),
				description: '',
				value: { l: 0, r: 0 }
			}
		} as Property,

		value: 0
	} as Property
};

const topCovers = {
	topCoverMaterial: {
		display: 'Top Cover Material',
		options: [
			'EVA Black 35 Durometer 1/16”',
			'EVA Black 35 Durometer 1/8”',
			'Neolon Black 1/16” with Bottom Cover',
			'Neolon Black 1/8” with Bottom Cover',
			'Plastazote Black 1/8"',
			'Poly-U w/Black vinyl 1/16”',
			'Poly-U w/Black vinyl 1/8”'
		],
		description:
			'Top Covers are used to create a soft interface between the foot and the 3D printed orthotic shell. A variety of materials offer differing effects. Poly-U and Neolon provide shock absorption and have good “memory”. EVA has excellent shock absorbing properties and will mold to the patient’s feet over time to improve comfort.  Plastazote is a standard material for patients with diabetes or arthritis due to it’s fast conformance to the patient’s feet which reduces high pressure areas and reduces shock.\n' +
			'\n' +
			'Our bottom cover is a very thin Black Micro-suede that covers 1” from the distal edge of the orthotic to the toes. It is not a full heel to toe cover bottom cover.',
		value: 0
	} as Property,

	topCoverLength: {
		display: 'Top Cover Length',
		options: ['Met Length', 'Sulcus Length', 'Full Length'],
		description:
			'The Top Cover length will determine which part of the foot will have padding underneath it. The length is measured from the back of the heel cup to a forward point. The metatarsal length is just behind the ball of the foot, the sulcus length is just behind the toes and the toe length is just past the end of the toes. In all instances Top Covers add a slight amount of bulk.',
		value: 0
	} as Property
};

const accomodations = {
	heelCushion: {
		display: 'Heel Cushion',
		options: NALBROptions,
		description:
			'Heel cushions are thin (either 1/16” or 1/8”) pads that are placed into the heel cup region of the orthoses. They are beveled along the forward edge and are used to provide an additional layer of cushioning directly underneath the calcaneus (heel bone). Deeper heel seats are often used in conjunction with Heel Cushions.',
		value: 0
	} as Property,

	mortonsExtEva: {
		display: "Morton's Ext (EVA)",
		options: NALBROptions,
		description:
			"Morton’s Extensions are made from 3mm EVA. They are placed at the medial distal edge of the orthoses and run under the hallux until they cross the interphalangeal joint. The Morton's Extension is helpful in treating patients with plantar 2nd metatarsal head symptoms and pain associated with hallux limitus but it will not impede forward propulsion during gait.",
		value: 0
	} as Property,

	heelSpurPad: {
		display: 'Heel Spur Pad',
		options: NALBROptions,
		description:
			'Heel Spur Pad is a horseshoe “U” shaped pad made from foam material and placed on top of the shell in the heel cup area. It’s purpose is to off-weight the center of the heel, provide additional shock absorption and to gather the fat pad under the heel. Deeper heel seats are often used in conjunction with heel spur pads.',
		value: 0
	} as Property,

	metBar: {
		display: 'Met Bar',
		options: NALBROptions,
		description:
			'Metatarsal Bars are pads made from foam materials that are placed behind the ball of the foot, usually behind the 2nd, 3rd and 4th metatarsal joint heads. They are used to reduce pain in the forefoot and work by  reducing the pressure and shearing forces that take place at the MPJ heads and by transferring weight away from sore areas and painful calluses.',
		value: 0
	} as Property,
	dancerPad: {
		display: "Dancer's Pad",
		options: NALBROptions,
		description:
			'Dancer’s Pad is made from foam materials and is placed behind the ball of the foot. The pad’s primary thickness is behind the 2nd and 3rd MPJ’s with a cut out for the 1st MPJ. This design transfers weight from the 1st MPJ to the lesser metatarsals making it effective in treating painful conditions of the 1st MPJ.',
		value: 0
	} as Property,

	metPads: {
		display: 'Met Pads (1/8")',
		options: NALBROptions,
		description:
			'Metatarsal Pads are 1/8” pads made from foam materials that are placed behind the ball of the foot, usually behind the 2nd, 3rd and 4th metatarsal joint heads. They are used to reduce pain in the forefoot and work by elevating the metatarsal bones and reducing the impact and shearing forces that take place at the MPJ heads and by transferring weight away from sore areas and painful calluses.',
		value: 0
	} as Property,

	neuromaPad: {
		display: 'Neuroma Pad',
		options: generateOptions(1, 4, 1, ''),
		description:
			'Neuroma pads are small pads made from foam materials that are placed between metatarsal heads. They are used to treat neuromas and other painful conditions which are caused by nerve tissue entrapment between the metatarsal bones. By separating these bones, pain, numbness and tingling can often be eliminated.',
		value: { l: [] as number[], r: [] as number[] }
	} as Property,

	metHeadCutOut: {
		display: 'Met Head Cut Out',
		options: generateOptions(1, 5, 1, ''),
		description:
			'Metatarsal Head Cut Outs are made from foam materials and are designed to take the weight off of painful metatarsal joints on the bottom of the foot. They are often shaped liked a “U” and are designed to elevate the two adjacent MPJ’s while leaving the area under the painful MPJ empty or in a “floating” state.',
		value: { l: [] as number[], r: [] as number[] }
	} as Property
};

const prescription = {
	shellModifications: {
		title: 'Shell',
		display: 'Shell Modifications',
		properties: shellModifications
	},
	postingOptions: {
		title: 'Posting',
		display: 'Posting options',
		properties: postingOptions
	},
	topCovers: {
		title: 'Top Covers',
		display: 'Coverings',
		properties: topCovers
	},
	accomodations: {
		title: 'Accommodations',
		display: 'Accommodations',
		properties: accomodations
	}
};

export default prescription;
